import React, { useState, useRef, useEffect } from 'react';
import { Button } from '../common/Button';

const MAX_MESSAGE_LENGTH = 2000;

/**
 * ChatInput - Message input field with auto-resize and character counter
 *
 * @param {Object} props
 * @param {Function} props.onSend - Callback when message is sent
 * @param {boolean} props.isSending - Whether a message is currently being sent
 * @param {boolean} props.disabled - Whether input is disabled
 * @param {Function} props.onClear - Callback when clear conversation is clicked
 * @param {boolean} props.isClearing - Whether conversation is being cleared
 */
const ChatInput = ({ onSend, isSending, disabled, onClear, isClearing }) => {
  const [message, setMessage] = useState('');
  const textareaRef = useRef(null);

  /**
   * Auto-resize textarea as user types
   */
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [message]);

  /**
   * Handle message send
   */
  const handleSend = () => {
    const trimmedMessage = message.trim();

    if (!trimmedMessage || isSending || disabled) {
      return;
    }

    if (trimmedMessage.length > MAX_MESSAGE_LENGTH) {
      return;
    }

    onSend(trimmedMessage);
    setMessage('');

    // Reset textarea height
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  };

  /**
   * Handle key press (Enter to send, Shift+Enter for newline)
   */
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const charCount = message.length;
  const isOverLimit = charCount > MAX_MESSAGE_LENGTH;
  const charsRemaining = MAX_MESSAGE_LENGTH - charCount;

  return (
    <div className="border-t bg-white p-4">
      {/* Character counter */}
      <div className="max-w-4xl mx-auto">
        <div className="flex justify-between items-center mb-2">
          <span className="hidden sm:inline text-xs text-gray-500">
            Press Enter to send, Shift+Enter for new line
          </span>
          <span
            className={`text-xs font-medium ${
              isOverLimit
                ? 'text-red-600'
                : charsRemaining < 100
                ? 'text-yellow-600'
                : 'text-gray-500'
            }`}
          >
            {charCount} / {MAX_MESSAGE_LENGTH}
          </span>
        </div>
      </div>

      {/* Input area */}
      <div className="max-w-4xl mx-auto">
        {/* Textarea and buttons */}
        <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:gap-2">
          <textarea
            ref={textareaRef}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Type your message..."
            disabled={disabled || isSending}
            className={`
              flex-1 resize-none rounded-lg border px-4 py-3
              focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent
              disabled:bg-gray-100 disabled:cursor-not-allowed
              ${isOverLimit ? 'border-red-500' : 'border-gray-300'}
            `}
            style={{
              minHeight: '48px',
              maxHeight: '200px'
            }}
            rows={1}
          />

          {/* Buttons row - stack on mobile, inline on larger screens */}
          <div className="flex gap-2 flex-shrink-0">
            <Button
              onClick={handleSend}
              disabled={
                disabled ||
                isSending ||
                !message.trim() ||
                isOverLimit
              }
              className="flex-1 sm:flex-none px-6 py-3"
            >
              {isSending ? (
                <span className="flex items-center justify-center gap-2">
                  <svg
                    className="animate-spin h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  <span className="hidden sm:inline">Sending...</span>
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  Send
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
                    />
                  </svg>
                </span>
              )}
            </Button>

            {onClear && (
              <Button
                onClick={onClear}
                disabled={isClearing}
                variant="primary"
                className="flex-1 sm:flex-none px-4 sm:px-6 py-3"
              >
                {isClearing ? 'Clearing...' : <span>Clear<span className="hidden sm:inline"> Conversation</span></span>}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Error message for over limit */}
      <div className="max-w-4xl mx-auto">
        {isOverLimit && (
          <p className="text-xs text-red-600 mt-2">
            Message is too long. Please reduce by {-charsRemaining} characters.
          </p>
        )}
      </div>
    </div>
  );
};

export default ChatInput;
